import {useCallback,useEffect,useMemo,useRef,useState} from 'react';
import {useAuth} from '../authContext';
import type {PhotoVideo} from './contracts';
import {fetchPhotoVideo} from './video';

export function usePhotoVideo(metadata:PhotoVideo|undefined,enabled=true){
 const {user,idToken,privileges}=useAuth(),[wake,setWake]=useState(0);
 const scope=JSON.stringify([user?.email,user?.status,user?.farm_ids,user?.roles,idToken,privileges,metadata?.assetId,metadata?.playbackPath,metadata?.byteLength,metadata?.width,metadata?.height,enabled,wake]);
 const current=useRef(scope);current.current=scope;
 const [state,setState]=useState<{scope:string;url?:string;error?:string;progress?:number}>({scope:''});
 const failed=useRef(false);failed.current=state.scope===scope&&!!state.error;
 const retry=useCallback(()=>setWake(value=>value+1),[]);
 useEffect(()=>{
  // Refocusing a healthy player must not discard its bytes or restart a download.
  // Account/access, visibility and connectivity changes still clear private media.
  const focus=()=>{if(failed.current)retry();};
  for(const event of ['online','offline','sonata-photo-cache-purged'])window.addEventListener(event,retry);
  window.addEventListener('focus',focus);document.addEventListener('visibilitychange',retry);
  return()=>{for(const event of ['online','offline','sonata-photo-cache-purged'])window.removeEventListener(event,retry);window.removeEventListener('focus',focus);document.removeEventListener('visibilitychange',retry);};
 },[retry]);
 const available=enabled&&!!metadata?.playbackPath&&user?.status==='Active'&&!!idToken&&navigator.onLine&&document.visibilityState!=='hidden';
 useEffect(()=>{
  const abort=new AbortController();let url:string|undefined,lastProgress=-1;setState({scope});
  if(available&&metadata)void fetchPhotoVideo(metadata,abort.signal,(loaded,total)=>{
   const progress=Math.min(99,Math.floor(loaded/total*100));
   if(!abort.signal.aborted&&current.current===scope&&progress!==lastProgress){lastProgress=progress;setState({scope,progress});}
  }).then(blob=>{
   if(abort.signal.aborted||current.current!==scope)return;
   url=URL.createObjectURL(blob);setState({scope,url});
  }).catch(()=>{if(!abort.signal.aborted&&current.current===scope)setState({scope,error:'Video unavailable. You can still view the photo.'});});
  return()=>{abort.abort();if(url)URL.revokeObjectURL(url);};
 },[scope,available]);
 const active=available&&state.scope===scope?state:undefined;
 const video=useMemo(()=>active?.url&&metadata?.width&&metadata?.height?{src:active.url,type:'video/mp4',width:metadata.width,height:metadata.height}:undefined,[active?.url,metadata?.width,metadata?.height]);
 return {video,available,loading:available&&!active?.url&&!active?.error,progress:active?.progress,error:active?.error,retry};
}
